import {
    dispatchWarningToast,
    dispatchSuccessToast,
    dispatchErrorToast,
} from 'src/store/modules/utils/toast'

/**
 * ReliableWebSocket - Enhanced WebSocket client with automatic reconnection and heartbeat support
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Server heartbeat/ping handling with pong responses
 * - Server ping timeout detection with configurable missed ping threshold
 * - Configurable reconnection attempts and delays
 * - Graceful handling of connection failures
 * - Event-based callbacks for connection lifecycle
 * - Toast notifications for connection status changes
 */

class ReliableWebSocket {
    constructor(url, options = {}) {
        this.url = url
        this.options = {
            heartbeatInterval: options.heartbeatInterval,
            reconnectDelayMin: options.reconnectDelayMin,
            reconnectDelayMax: options.reconnectDelayMax,
            maxReconnectAttempts: options.maxReconnectAttempts,
            showToastNotifications: options.showToastNotifications !== false, // default true
            dispatch: options.dispatch, // Redux dispatch function for toast notifications
            maxConsecutiveMissedPings: options.maxConsecutiveMissedPings,
            pingTimeoutBufferMs: options.pingTimeoutBufferMs,
            ...options,
        }

        this.ws = null
        this.reconnectAttempts = 0
        this.reconnectTimer = null
        this.isIntentionallyClosed = false
        this.isReconnecting = false

        // Server ping tracking
        this.pingTimeoutTimer = null
        this.consecutiveMissedPings = 0
        this.lastPingReceived = null
    }

    connect() {
        try {
            // Clean up existing WebSocket if present
            if (this.ws) {
                this.ws.onopen = null
                this.ws.onclose = null
                this.ws.onmessage = null
                this.ws.onerror = null
                if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
                    this.ws.close()
                }
            }

            this.ws = new WebSocket(this.url, [
                'Authorization',
                this.getToken(),
            ])
            this.setupEventHandlers()
        } catch (error) {
            console.error('WebSocket connection failed:', error)
            this.scheduleReconnect()
        }
    }

    showToast(toastType, message, options = {}) {
        if (this.options.showToastNotifications && this.options.dispatch) {
            const toastHandlers = {
                success: dispatchSuccessToast,
                warning: dispatchWarningToast,
                error: dispatchErrorToast,
            }
            toastHandlers[toastType]?.(this.options.dispatch, message, options)
        }
    }

    setupEventHandlers() {
        this.ws.onopen = () => {
            console.log('WebSocket connected')

            if (this.isReconnecting) {
                this.showToast(
                    'success',
                    'Connection restored. Please refresh the page to see the latest version of the runbook.',
                    { timeout: 0 }
                )
            }

            this.reconnectAttempts = 0
            this.isReconnecting = false
            this.consecutiveMissedPings = 0
            this.startPingTimeoutMonitoring()
            this.options.onOpen?.()
        }

        this.ws.onmessage = (event) => {
            const message = JSON.parse(event.data)

            if (message.type === 'ping') {
                this.handlePingReceived()
                this.ws.send(
                    JSON.stringify({ type: 'pong', timestamp: Date.now() })
                )
                return
            }

            this.options.onMessage?.(message)
        }

        this.ws.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason)

            this.clearPingTimeoutTimer()

            if (!this.isIntentionallyClosed) {
                this.scheduleReconnect()
            }

            this.options.onClose?.(event)
        }

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error)
            this.options.onError?.(error)
        }
    }

    scheduleReconnect() {
        if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached')
            this.showToast(
                'error',
                'Connection lost. Unable to reconnect automatically. Please refresh the page.',
                { timeout: 0 }
            )
            return
        }

        const delay = this.calculateReconnectDelay()
        console.log(
            `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`
        )

        const delaySeconds = Math.round(delay / 1000)
        const attemptText =
            this.reconnectAttempts === 0
                ? 'Connection lost. Attempting to reconnect'
                : `Reconnection failed. Retrying (attempt ${
                      this.reconnectAttempts + 1
                  }/${this.options.maxReconnectAttempts})`

        this.showToast(
            'warning',
            `${attemptText} in ${delaySeconds} second${
                delaySeconds > 1 ? 's' : ''
            }...`,
            { timeout: delay }
        )
        this.isReconnecting = true

        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++
            this.reconnectTimer = null // Clear the timer reference
            console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.options.maxReconnectAttempts}`)
            this.connect()
        }, delay)
    }

    calculateReconnectDelay() {
        // Exponential backoff
        const { reconnectDelayMin, reconnectDelayMax } = this.options
        if (typeof reconnectDelayMin !== 'number' || typeof reconnectDelayMax !== 'number') {
            throw new Error('ReliableWebSocket requires reconnectDelayMin and reconnectDelayMax to be defined as numbers')
        }
        return Math.min(
            reconnectDelayMin * Math.pow(2, this.reconnectAttempts),
            reconnectDelayMax
        )
    }

    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(typeof data === 'string' ? data : JSON.stringify(data))
        }
    }

    close() {
        this.isIntentionallyClosed = true
        this.isReconnecting = false
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
        }
        this.clearPingTimeoutTimer()
        if (this.ws) {
            this.ws.close()
        }
    }

    startPingTimeoutMonitoring() {
        if (!this.options.heartbeatInterval || !this.options.pingTimeoutBufferMs || !this.options.maxConsecutiveMissedPings) {
            return // Skip ping monitoring if not configured
        }

        this.resetPingTimeout()
    }

    resetPingTimeout() {
        this.clearPingTimeoutTimer()
        const timeoutDuration = this.options.heartbeatInterval + this.options.pingTimeoutBufferMs

        this.pingTimeoutTimer = setTimeout(() => {
            this.handlePingTimeout()
        }, timeoutDuration)
    }

    clearPingTimeoutTimer() {
        if (this.pingTimeoutTimer) {
            clearTimeout(this.pingTimeoutTimer)
            this.pingTimeoutTimer = null
        }
    }

    handlePingReceived() {
        this.lastPingReceived = Date.now()
        this.consecutiveMissedPings = 0
        this.resetPingTimeout()
    }

    handlePingTimeout() {
        this.consecutiveMissedPings++
        console.warn(`Server ping timeout ${this.consecutiveMissedPings}/${this.options.maxConsecutiveMissedPings}`)

        if (this.consecutiveMissedPings >= this.options.maxConsecutiveMissedPings) {
            console.error('Max consecutive missed pings reached, forcing reconnection')
            this.forceReconnect()
        } else {
            // Reset timeout for next ping
            this.resetPingTimeout()
        }
    }

    forceReconnect() {
        this.clearPingTimeoutTimer()
        this.consecutiveMissedPings = 0

        if (this.ws) {
            this.ws.close()
        }
    }

    getToken() {
        // Get token from axios default headers, similar to existing websockets.js
        try {
            return this.options.getToken?.() || ''
        } catch (error) {
            console.warn('Failed to get WebSocket token:', error)
            return ''
        }
    }
}

export default ReliableWebSocket
